import { useTheme } from '@mui/material';
import { useMemo } from 'react';
import moment from 'moment';
import { WARNING_DAYS } from '../config';
import { ItemDate } from '../components';

export type Status =
  | 'ontrack'
  | 'complete'
  | 'warning'
  | 'pastdue'
  | 'error'
  | 'default';

export const useChipStatusColors = (color: Status) => {
  const theme = useTheme();
  // TODO: theme doesn't work here????
  // need to figure out
  theme.palette.primary.main = '#163B3F';
  theme.palette.secondary.main = '#76B041';
  return useMemo(() => {
    switch (color) {
      case 'ontrack':
        return [
          theme.palette.secondary.main,
          `transparent`,
          theme.palette.secondary.main,
        ];
      case 'complete':
        return [
          theme.palette.primary.main,
          `${theme.palette.primary.main}1f`,
          'black',
        ];
      case 'warning':
        return [
          theme.palette.warning.main,
          `transparent`,
          theme.palette.warning.main,
          ,
        ];
      case 'pastdue':
      case 'error':
        return [
          theme.palette.error.main,
          `transparent`,
          theme.palette.error.main,
        ];
      default:
        return [
          theme.palette.text.secondary,
          'transparent',
          theme.palette.text.secondary,
        ];
    }
  }, [color]);
};

export const getStatusFromDate = (date?: Date): Status => {
  if (date) {
    let days = moment(date).diff(moment.now(), 'days');
    return days <= WARNING_DAYS
      ? days < 0
        ? 'pastdue'
        : 'warning'
      : 'ontrack';
  }
  return 'default';
};

export type ComponentColors = {
  border?: string;
  background?: string;
  font?: string;
};

export const useItemColors = ({
  date,
  complete,
}: {
  date?: Date | string | null;
  complete?: boolean;
}): {
  datechip: ComponentColors;
  status: ComponentColors;
  item: ComponentColors;
} => {
  const theme = useTheme();
  // TODO: theme doesn't work here????
  // need to figure out
  theme.palette.primary.main = '#163B3F';
  theme.palette.secondary.main = '#76B041';
  return useMemo(() => {
    let status = complete ? 'complete' : 'default';
    if (date && !complete) {
      let days = moment(date).diff(moment.now(), 'days');
      if (days <= WARNING_DAYS) {
        if (days < 0) {
          status = 'pastdue';
        } else {
          status = 'warning';
        }
      } else {
        status = 'ontrack';
      }
    }

    switch (status) {
      case 'ontrack':
        return {
          status: {
            border: theme.palette.secondary.main,
            background: theme.palette.secondary.main,
            font: 'white',
          },
          datechip: {
            border: theme.palette.secondary.main,
            background: `transparent`,
            font: theme.palette.secondary.main,
          },
          item: {
            border: theme.palette.secondary.main,
            background: `${theme.palette.secondary.main}1f`,
            font: theme.palette.secondary.main,
          },
        };
      case 'complete':
        return {
          status: {
            border: theme.palette.primary.main,
            background: theme.palette.primary.main,
            font: 'white',
          },
          datechip: {
            border: theme.palette.primary.main,
            background: `transparent`,
            font: 'black',
          },
          item: {
            border: theme.palette.primary.main,
            background: `${theme.palette.primary.main}1f`,
            font: theme.palette.primary.main,
          },
        };
      case 'warning':
        return {
          status: {
            border: theme.palette.warning.main,
            background: theme.palette.warning.main,
            font: 'white',
          },
          datechip: {
            border: theme.palette.warning.main,
            background: `transparent`,
            font: theme.palette.warning.main,
          },
          item: {
            border: theme.palette.warning.main,
            background: `${theme.palette.warning.main}1f`,
            font: theme.palette.warning.main,
          },
        };
      case 'pastdue':
      case 'error':
        return {
          status: {
            border: theme.palette.error.main,
            background: theme.palette.error.main,
            font: 'white',
          },
          datechip: {
            border: theme.palette.error.main,
            background: `transparent`,
            font: theme.palette.error.main,
          },
          item: {
            border: theme.palette.error.main,
            background: `${theme.palette.error.main}1f`,
            font: theme.palette.error.main,
          },
        };
      default:
        return {
          status: {
            border: theme.palette.text.secondary,
            background: theme.palette.text.secondary,
            font: 'white',
          },
          datechip: {
            border: undefined,
            background: 'transparent',
            font: theme.palette.text.secondary,
          },
          item: {
            border: theme.palette.text.secondary,
            background: `${theme.palette.text.secondary}1f`,
            font: theme.palette.text.secondary,
          },
        };
    }
  }, [date, complete]);
};

export const getItemColors = ({
  date,
  complete,
}: {
  date?: Date | string | null;
  complete?: boolean;
}): {
  datechip: ComponentColors;
  status: ComponentColors;
  item: ComponentColors;
} => {
  const theme = useTheme();
  // TODO: theme doesn't work here????
  // need to figure out
  theme.palette.primary.main = '#163B3F';
  theme.palette.secondary.main = '#76B041';

  let status = complete ? 'complete' : 'default';
  if (date && !complete) {
    let days = moment(date).diff(moment.now(), 'days');
    if (days <= WARNING_DAYS) {
      if (days < 0) {
        status = 'pastdue';
      } else {
        status = 'warning';
      }
    } else {
      status = 'ontrack';
    }
  }
  switch (status) {
    case 'ontrack':
      return {
        status: {
          border: theme.palette.secondary.main,
          background: theme.palette.secondary.main,
          font: 'white',
        },
        datechip: {
          border: theme.palette.secondary.main,
          background: `transparent`,
          font: theme.palette.secondary.main,
        },
        item: {
          border: theme.palette.secondary.main,
          background: `${theme.palette.secondary.main}1f`,
          font: theme.palette.secondary.main,
        },
      };
    case 'complete':
      return {
        status: {
          border: theme.palette.primary.main,
          background: theme.palette.primary.main,
          font: 'white',
        },
        datechip: {
          border: theme.palette.primary.main,
          background: `transparent`,
          font: 'black',
        },
        item: {
          border: theme.palette.primary.main,
          background: `${theme.palette.primary.main}1f`,
          font: theme.palette.primary.main,
        },
      };
    case 'warning':
      return {
        status: {
          border: theme.palette.warning.main,
          background: theme.palette.warning.main,
          font: 'white',
        },
        datechip: {
          border: theme.palette.warning.main,
          background: `transparent`,
          font: theme.palette.warning.main,
        },
        item: {
          border: theme.palette.warning.main,
          background: `${theme.palette.warning.main}1f`,
          font: theme.palette.warning.main,
        },
      };
    case 'pastdue':
    case 'error':
      return {
        status: {
          border: theme.palette.error.main,
          background: theme.palette.error.main,
          font: 'white',
        },
        datechip: {
          border: theme.palette.error.main,
          background: `transparent`,
          font: theme.palette.error.main,
        },
        item: {
          border: theme.palette.error.main,
          background: `${theme.palette.error.main}1f`,
          font: theme.palette.error.main,
        },
      };
    default:
      return {
        status: {
          border: theme.palette.text.secondary,
          background: theme.palette.text.secondary,
          font: 'white',
        },
        datechip: {
          border: undefined,
          background: 'transparent',
          font: theme.palette.text.secondary,
        },
        item: {
          border: theme.palette.text.secondary,
          background: `${theme.palette.text.secondary}1f`,
          font: theme.palette.text.secondary,
        },
      };
  }
};
